import React, { useContext, useEffect, useRef, useState } from 'react';
import { arrayOf, bool, func, shape } from 'prop-types';
import { Box, useMediaQuery } from '@mui/material';
import { CancelToken } from 'axios';
import { useParams } from 'react-router-dom';
import { useSetNoAuthCard, useSetSelectorIndex, useURLRouting } from 'hooks';

// Context
import { AuthContext } from 'authentication/AuthContext';

// Components
import { NoAuthorizationsAvailable } from 'feature/loading-management/shared-components';
import Filters from '../../filters/Filters.connector';
import MobileFilters from '../../filters/MobileFilters';
import SelectorsList from './selectors-list/SelectorsList.connector';

// Utils
import { sidebarStyles, SUPPLIER_VIEW_BY_OPTIONS } from '../../utils/const';

const Sidebar = ({
  currentSelection,
  fetchSupplierAuthorizations,
  fetchUniqueSelectors,
  resetSupplierAuth,
  selectorsList,
  setFilters,
  selectorsListLoading,
  setSupplierAuthorizationsPending,
}) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const fetchUniqueSelectorsToken = useRef();
  const selectorsCancelToken = useRef();
  const previousCardFetch = useRef();

  const { viewBy, viewById } = useParams();

  const { currentAffiliate } = useContext(AuthContext);

  const [refetch, setRefetch] = useState(false);

  // this will updated the currentSelection when the user hits the back button
  useURLRouting(currentSelection, selectorsList, viewById, setFilters);

  // This is to update our selector index based off of the URL
  useSetSelectorIndex(selectorsList, setFilters);

  useSetNoAuthCard(selectorsList, selectorsListLoading, setSupplierAuthorizationsPending, refetch);

  useEffect(() => {
    if (currentAffiliate?.customerIdName) {
      previousCardFetch.current = null;
      if (fetchUniqueSelectorsToken.current?.cancel) {
        fetchUniqueSelectorsToken.current.cancel();
        // this bool is so that if we come in and refetch
        // the selectors we don't show the no availaible
        // card in the cards panel
        setRefetch(true);
      }
      const cancelToken = CancelToken.source();
      fetchUniqueSelectorsToken.current = cancelToken;
      fetchUniqueSelectors(
        {
          affiliate: {
            companyCustomerIdName: currentAffiliate?.customerIdName,
            dataCompanyId: currentAffiliate?.dataCompanyId,
          },
        },
        viewBy,
        cancelToken.token
      )
        // Fetch is a promise if we get some data back
        // set the refetch to false so that the cards
        // panel will render correctly
        .then(res => res?.length && setRefetch(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAffiliate?.customerIdName, currentAffiliate?.dataCompanyId, viewBy]);

  useEffect(() => {
    const getParamsObj = () => {
      const optionalParam =
        viewBy === 'supplier'
          ? {
              supplier: {
                companyCustomerIdName: currentSelection.idName,
                dataCompanyId: currentSelection.dataCompanyId,
              },
            }
          : {
              [`${viewBy}Id`]: currentSelection[`${viewBy}Id`],
            };
      return {
        affiliate: {
          companyCustomerIdName: currentAffiliate.customerIdName,
          dataCompanyId: currentAffiliate.dataCompanyId,
        },
        ...optionalParam,
      };
    };

    if (currentSelection.nameShort && previousCardFetch.current !== currentSelection.nameShort) {
      previousCardFetch.current = currentSelection.nameShort;
      if (selectorsCancelToken.current?.cancel) {
        selectorsCancelToken.current.cancel();
      }
      const cancelToken = CancelToken.source();
      selectorsCancelToken.current = cancelToken;
      const params = getParamsObj();
      fetchSupplierAuthorizations(params, viewBy, cancelToken.token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelection.nameShort]);

  useEffect(() => {
    return () => resetSupplierAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box {...sidebarStyles}>
      {lgUp ? (
        <Filters loading={selectorsListLoading} viewByOptions={SUPPLIER_VIEW_BY_OPTIONS} />
      ) : (
        <MobileFilters>
          <Filters loading={selectorsListLoading} viewByOptions={SUPPLIER_VIEW_BY_OPTIONS} />
        </MobileFilters>
      )}
      {!lgUp && !selectorsList.length && !selectorsListLoading ? (
        <Box padding={3}>
          <NoAuthorizationsAvailable />
        </Box>
      ) : (
        <SelectorsList selectorsList={selectorsList} selectorsListLoading={selectorsListLoading} />
      )}
    </Box>
  );
};

Sidebar.propTypes = {
  currentSelection: shape({}),
  fetchSupplierAuthorizations: func.isRequired,
  fetchUniqueSelectors: func.isRequired,
  resetSupplierAuth: func.isRequired,
  selectorsList: arrayOf(shape({})).isRequired,
  selectorsListLoading: bool.isRequired,
  setFilters: func.isRequired,
  setSupplierAuthorizationsPending: func,
};

Sidebar.defaultProps = {
  currentSelection: {},
  setSupplierAuthorizationsPending: () => {},
};

export default Sidebar;
